import { useState, useEffect } from 'react'
import { sendElementEmail } from '../../api'

interface ElementComposeWindowProps {
  readonly isComposeWindowOpen: boolean
  readonly initialToAddress?: string
  readonly initialSubjectLine?: string
  readonly initialBodyContent?: string
  readonly onComposeWindowClose: () => void
  readonly onEmailSentSuccessfully: () => void
}

interface ElementComposeFormState {
  toAddressInput: string
  ccAddressInput: string
  subjectLineInput: string
  emailBodyInput: string
}

const ELEMENT_COMPOSE_FORM_INITIAL_STATE: ElementComposeFormState = {
  toAddressInput: '',
  ccAddressInput: '',
  subjectLineInput: '',
  emailBodyInput: ''
}

export function ElementComposeWindow({
  isComposeWindowOpen,
  initialToAddress = '',
  initialSubjectLine = '',
  initialBodyContent = '',
  onComposeWindowClose,
  onEmailSentSuccessfully
}: ElementComposeWindowProps) {
  const [composeFormState, setComposeFormState] = useState<ElementComposeFormState>({
    ...ELEMENT_COMPOSE_FORM_INITIAL_STATE,
    toAddressInput: initialToAddress,
    subjectLineInput: initialSubjectLine,
    emailBodyInput: initialBodyContent
  })
  const [isEmailSending, setIsEmailSending] = useState<boolean>(false)
  const [emailSendError, setEmailSendError] = useState<string | null>(null)
  const [emailSendSuccessMessage, setEmailSendSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    if (isComposeWindowOpen) {
      setComposeFormState({
        ...ELEMENT_COMPOSE_FORM_INITIAL_STATE,
        toAddressInput: initialToAddress,
        subjectLineInput: initialSubjectLine,
        emailBodyInput: initialBodyContent
      })
      setEmailSendError(null)
      setEmailSendSuccessMessage(null)
    }
  }, [isComposeWindowOpen, initialToAddress, initialSubjectLine, initialBodyContent])

  function updateComposeFormField(
    fieldName: keyof ElementComposeFormState,
    updatedFieldValue: string
  ): void {
    setComposeFormState(previousFormState => ({
      ...previousFormState,
      [fieldName]: updatedFieldValue
    }))
  }

  async function handleElementEmailSendSubmit(): Promise<void> {
    const { toAddressInput, ccAddressInput, subjectLineInput, emailBodyInput } = composeFormState

    if (!toAddressInput.trim()) {
      setEmailSendError('Please enter at least one recipient address')
      return
    }
    if (!subjectLineInput.trim()) {
      setEmailSendError('Please enter a subject line')
      return
    }

    setIsEmailSending(true)
    setEmailSendError(null)

    try {
      const parsedToAddresses = toAddressInput
        .split(',')
        .map(emailAddress => emailAddress.trim())
        .filter(emailAddress => emailAddress.length > 0)

      const parsedCcAddresses = ccAddressInput
        .split(',')
        .map(emailAddress => emailAddress.trim())
        .filter(emailAddress => emailAddress.length > 0)

      await sendElementEmail({
        toAddresses: parsedToAddresses,
        ccAddresses: parsedCcAddresses.length > 0 ? parsedCcAddresses : undefined,
        subjectLine: subjectLineInput,
        plainTextBody: emailBodyInput,
        htmlBody: `<pre style="font-family:inherit;white-space:pre-wrap">${emailBodyInput}</pre>`
      })

      setEmailSendSuccessMessage('Message sent successfully')
      setTimeout(() => {
        onEmailSentSuccessfully()
        onComposeWindowClose()
      }, 1200)

    } catch (emailSendError: unknown) {
      const errorMessage = emailSendError instanceof Error ? emailSendError.message : 'Failed to send message'
      setEmailSendError(errorMessage)
    } finally {
      setIsEmailSending(false)
    }
  }

  useEffect(() => {
    function handleEscapeKey(keyboardEvent: KeyboardEvent): void {
      if (keyboardEvent.key === 'Escape') {
        onComposeWindowClose()
      }
    }
    if (isComposeWindowOpen) {
      document.addEventListener('keydown', handleEscapeKey)
    }
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [isComposeWindowOpen, onComposeWindowClose])

  if (!isComposeWindowOpen) return null

  return (
    <dialog
      className="element-compose-window"
      aria-label="Compose new message"
      open
    >
      <div className="element-compose-header">
        <span className="element-compose-title">New Message</span>
        <button
          className="element-compose-close-button"
          onClick={onComposeWindowClose}
          aria-label="Close compose window"
        >
          ×
        </button>
      </div>

      <div className="element-compose-fields">
        <div className="element-compose-field-row">
          <label className="element-compose-field-label" htmlFor="element-compose-to">
            To
          </label>
          <input
            id="element-compose-to"
            type="email"
            multiple
            className="element-compose-field-input"
            placeholder="recipient@example.com"
            value={composeFormState.toAddressInput}
            onChange={changeEvent =>
              updateComposeFormField('toAddressInput', changeEvent.target.value)
            }
            autoFocus
          />
        </div>

        <div className="element-compose-field-row">
          <label className="element-compose-field-label" htmlFor="element-compose-cc">
            Cc
          </label>
          <input
            id="element-compose-cc"
            type="email"
            multiple
            className="element-compose-field-input"
            placeholder="cc@example.com"
            value={composeFormState.ccAddressInput}
            onChange={changeEvent =>
              updateComposeFormField('ccAddressInput', changeEvent.target.value)
            }
          />
        </div>

        <div className="element-compose-field-row">
          <label className="element-compose-field-label" htmlFor="element-compose-subject">
            Subject
          </label>
          <input
            id="element-compose-subject"
            type="text"
            className="element-compose-field-input"
            placeholder="Subject line"
            value={composeFormState.subjectLineInput}
            onChange={changeEvent =>
              updateComposeFormField('subjectLineInput', changeEvent.target.value)
            }
          />
        </div>
      </div>

      <textarea
        className="element-compose-body-textarea"
        placeholder="Write your message here..."
        value={composeFormState.emailBodyInput}
        onChange={changeEvent =>
          updateComposeFormField('emailBodyInput', changeEvent.target.value)
        }
        aria-label="Message body"
      />

      <div className="element-compose-action-bar">
        <button
          className="element-compose-send-button"
          onClick={handleElementEmailSendSubmit}
          disabled={isEmailSending}
        >
          {isEmailSending ? 'Sending...' : 'Send'}
        </button>
        <button
          className="element-compose-discard-button"
          onClick={onComposeWindowClose}
          disabled={isEmailSending}
        >
          Discard
        </button>
        {emailSendError && (
          <span className="element-compose-error-message">{emailSendError}</span>
        )}
        {emailSendSuccessMessage && (
          <span className="element-compose-success-message">{emailSendSuccessMessage}</span>
        )}
      </div>
    </dialog>
  )
}
